"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createRoomStatusObservable = exports.onRoomStatus = void 0;
const logger_1 = require("@streem/logger");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const streem_sdk_protobuf_1 = require("streem-sdk-protobuf");
const logger_2 = require("./logger");
function onRoomStatus({ status, source, }) {
    return source.pipe((0, operators_1.filter)((s) => status === s), (0, operators_1.take)(1));
}
exports.onRoomStatus = onRoomStatus;
function createRoomStatusObservable(roomStatusSource, localParticipantStatusSource, remoteParticipantStatusSource, invitationStatusSource) {
    return (0, rxjs_1.combineLatest)([
        roomStatusSource,
        localParticipantStatusSource,
        remoteParticipantStatusSource,
        invitationStatusSource,
    ]).pipe((0, operators_1.map)(([roomStatus, localParticipantStatus, remoteParticipantStatus, invitationStatus]) => buildRoomSessionStatus({
        roomStatus,
        invitationStatus,
        localParticipantStatus,
        remoteParticipantStatus,
    })), (0, operators_1.distinctUntilChanged)((x, y) => x === y));
}
exports.createRoomStatusObservable = createRoomStatusObservable;
// note that there is overlap between the various conditions, but
// it was felt that it improves readability to spell each condition out
// explicitly and not use one condition inside another, for example
const buildRoomSessionStatus = (config) => {
    logger_2.logger.setContextValue(logger_1.contextValues.SESSION_STATUS_CONFIG, config);
    if (checkDisconnectedConditions(config)) {
        return 'DISCONNECTED';
    }
    if (checkConnectedConditions(config)) {
        return 'CONNECTED';
    }
    if (checkConnectingConditions(config)) {
        return 'CONNECTING';
    }
    if (checkDisconnectingConditions(config)) {
        return 'DISCONNECTING';
    }
    logger_2.logger.warn('Unable to determine room session status, received status', config);
    return 'UNKNOWN';
};
const checkDisconnectedConditions = (config) => {
    const { roomStatus, remoteParticipantStatus, invitationStatus } = config;
    const retVal = (roomStatus === streem_sdk_protobuf_1.streem.api.Room.Status.STATUS_CLOSED ||
        invitationStatus === streem_sdk_protobuf_1.streem.api.RoomInvitation.Status.STATUS_REJECTED ||
        invitationStatus === streem_sdk_protobuf_1.streem.api.RoomInvitation.Status.STATUS_CANCELED ||
        invitationStatus === streem_sdk_protobuf_1.streem.api.RoomInvitation.Status.STATUS_TIMED_OUT ||
        /**
         * If the back end finds that a participant has not sent heartbeats for two minutes, it will update the room
         * participant's status to LEFT, but it will not update the room status. So, we have to watch the remote
         * participant's status here and end the call if they've left. Otherwise, we'll just sit here like a fool
         * talking to nobody forever until the end of time.
         */
        remoteParticipantStatus === streem_sdk_protobuf_1.streem.api.RoomParticipant.Status.STATUS_LEFT);
    if (retVal) {
        logger_2.logger.debug(`Room DISCONNECTED Conditions Met: roomStatus=${roomStatus} remoteParticipantStatus=${remoteParticipantStatus} invitationStatus=${invitationStatus}`);
    }
    return retVal;
};
const checkConnectingConditions = (config) => {
    const { roomStatus, invitationStatus, localParticipantStatus, remoteParticipantStatus, } = config;
    const retVal = (roomStatus !== streem_sdk_protobuf_1.streem.api.Room.Status.STATUS_CLOSED && // can't be connecting if the room is closed
        (localParticipantStatus !== streem_sdk_protobuf_1.streem.api.RoomParticipant.Status.STATUS_ACTIVE ||
            remoteParticipantStatus !== streem_sdk_protobuf_1.streem.api.RoomParticipant.Status.STATUS_ACTIVE) && // if either user isn't in
        invitationStatus !== streem_sdk_protobuf_1.streem.api.RoomInvitation.Status.STATUS_REJECTED && // we're disconnected
        invitationStatus !== streem_sdk_protobuf_1.streem.api.RoomInvitation.Status.STATUS_CANCELED && // we're disconnected
        invitationStatus !== streem_sdk_protobuf_1.streem.api.RoomInvitation.Status.STATUS_TIMED_OUT);
    if (retVal) {
        logger_2.logger.debug(`Room CONNECTING Conditions Met: roomStatus=${roomStatus} localParticipantStatus=${localParticipantStatus} remoteParticipantStatus=${remoteParticipantStatus} invitationStatus=${invitationStatus}`);
    }
    return retVal;
};
const checkConnectedConditions = (config) => {
    const { roomStatus, localParticipantStatus, remoteParticipantStatus } = config;
    // the room is open and both users are in it, were connected
    // this means if say the remote user were to leave, we would not be connected,
    // which is what we want (even though the local user is)
    const retVal = (localParticipantStatus === streem_sdk_protobuf_1.streem.api.RoomParticipant.Status.STATUS_ACTIVE &&
        remoteParticipantStatus === streem_sdk_protobuf_1.streem.api.RoomParticipant.Status.STATUS_ACTIVE &&
        roomStatus === streem_sdk_protobuf_1.streem.api.Room.Status.STATUS_OPEN);
    if (retVal) {
        logger_2.logger.debug(`Room CONNECTED Conditions Met: roomStatus=${roomStatus} localParticipantStatus=${localParticipantStatus} remoteParticipantStatus=${remoteParticipantStatus}`);
    }
    return retVal;
};
const checkDisconnectingConditions = (config) => {
    const { roomStatus, invitationStatus, localParticipantStatus } = config;
    const retVal = (roomStatus !== streem_sdk_protobuf_1.streem.api.Room.Status.STATUS_CLOSED && // closed would mean already disconnected
        ![
            undefined,
            streem_sdk_protobuf_1.streem.api.RoomParticipant.Status.STATUS_CONNECTED,
            streem_sdk_protobuf_1.streem.api.RoomParticipant.Status.STATUS_ACTIVE,
            streem_sdk_protobuf_1.streem.api.RoomParticipant.Status.STATUS_NEVER_ARRIVED
        ].includes(localParticipantStatus) &&
        invitationStatus !== streem_sdk_protobuf_1.streem.api.RoomInvitation.Status.STATUS_REJECTED && // never connected
        invitationStatus !== streem_sdk_protobuf_1.streem.api.RoomInvitation.Status.STATUS_CANCELED && // never connected
        invitationStatus !== streem_sdk_protobuf_1.streem.api.RoomInvitation.Status.STATUS_TIMED_OUT);
    if (retVal) {
        logger_2.logger.debug(`Room DISCONNECTING Conditions Met: roomStatus=${roomStatus} localParticipantStatus=${localParticipantStatus} invitationStatus=${invitationStatus}`);
    }
    return retVal;
};
//# sourceMappingURL=create_room_status_observable.js.map